import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  TextField,
  Avatar,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Tooltip,
  Badge
} from '@mui/material';
import MainLayout from '../../layout/MainLayout';
import FavoriteButton from '../../components/olymp/FavoriteButton';
import CartButton from '../../components/olymp/CartButton';
import SensitivityBadge from '../../components/olymp/SensitivityBadge';
import DataExplorationDetail from '../../components/olymp/DataExplorationDetail';
import WerteverteilungCard from './data_catalog/components/WerteverteilungCard';
import OmopMappingDetail from '../../components/olymp/OmopMappingDetail';
import TagsSynonymsDetail from '../../components/olymp/TagsSynonymsDetail';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoIcon from '@mui/icons-material/Info';
import CommentIcon from '@mui/icons-material/Comment';
import SendIcon from '@mui/icons-material/Send';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import ScienceIcon from '@mui/icons-material/Science';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import TranslateIcon from '@mui/icons-material/Translate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTranslation } from 'react-i18next';
import { useAppearance } from '../../context/AppearanceContext';
import {
  approveCatalogTag,
  approveCatalogSynonym,
  commentCatalogSynonym,
  commentCatalogTag,
  commentCurrentMapping,
  commentMappingProposal,
  createCatalogSynonym,
  createCatalogTag,
  deleteCatalogSynonym,
  deleteCatalogTag,
  getParameter,
  getParameterLookupId,
  rejectCatalogSynonym,
  rejectCatalogTag,
  approveMappingProposal,
  createMappingProposal,
  rejectMappingProposal,
  voteCatalogSynonym,
  voteCatalogTag,
  voteCurrentMapping,
  voteMappingProposal,
} from '../../services/catalogService';
import { getResolvedOlympAccess } from '../../utils/olympAccess';

const HeaderSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const buildTagsFromParameter = (parameter) => {
  if (Array.isArray(parameter?.tagDetails) && parameter.tagDetails.length > 0) {
    return parameter.tagDetails.map(tag => ({
      id: tag.id,
      name: tag.name,
      icon: tag.icon,
      validated: Boolean(tag.validated ?? tag.status === 'approved'),
      isPrivate: Boolean(tag.isPrivate),
      status: tag.status || (tag.validated ? 'approved' : 'pending_review'),
      suggestedBy: tag.suggestedBy || tag.createdBy || '',
      approvedBy: tag.approvedBy || '',
      likes: tag.likes || 0,
      dislikes: tag.dislikes || 0,
      likedBy: tag.likedBy || [],
      dislikedBy: tag.dislikedBy || [],
      comments: tag.comments || [],
    }));
  }

  return (parameter?.tags || []).map(tagName => ({
    id: null,
    name: tagName,
    icon: null,
    validated: true,
    isPrivate: false,
  }));
};

const PARAMETER_DETAIL_COPY = {
  de: {
    loading: 'Lade Parameter-Daten...',
    loadError: 'Parameterdaten konnten nicht aus dem Backend geladen werden.',
    noDetails: 'Für diesen Parameter sind keine Detaildaten verfügbar.',
    backToCatalog: 'Zurück zum Katalog',
    approvedSynonyms: 'Bestätigte Synonyme',
    moreSynonyms: '+{{count}} weitere...',
    categories: 'Kategorien',
    statusAndProperties: 'Status & Eigenschaften',
    automaticCapture: 'Automatische Erfassung',
    availableSince: 'Verfügbar seit {{date}}',
    tabs: {
      availability: 'Verfügbarkeit & Daten',
      mapping: 'OMOP Mapping',
      tags: 'Tags & Synonyme',
      documentation: 'Dokumentation',
      comments: 'Kommentare',
    },
    documentationTitle: 'Dokumentation & Hinweise',
    sourceSystem: 'Quellsystem',
    system: 'System',
    updateFrequency: 'Update-Frequenz',
    lastSync: 'Letzter Sync',
    reliability: 'Zuverlässigkeit',
    engineerNotes: 'Data Engineer Notizen',
    noNotes: 'Keine zusätzlichen Notizen vorhanden.',
    commentsTitle: 'Kommentare & Diskussion',
    addCommentPlaceholder: 'Kommentar hinzufügen...',
    sendComment: 'Kommentar senden',
    noComments: 'Keine Kommentare vorhanden.',
    unknown: 'Unbekannt',
    usagePatterns: {
      always: 'Immer',
      frequent: 'Häufig',
      specific: 'Spezifisch',
      occasional: 'Gelegentlich',
      rare: 'Selten',
      unknown: 'Unbekannt',
    },
    omopStatus: {
      validated: 'OMOP Validiert',
      mapped: 'OMOP Gemappt',
      pending: 'OMOP Ausstehend',
      unmapped: 'OMOP Nicht gemappt',
    },
  },
  en: {
    loading: 'Loading parameter data...',
    loadError: 'Parameter data could not be loaded from the backend.',
    noDetails: 'No detail data is available for this parameter.',
    backToCatalog: 'Back to catalog',
    approvedSynonyms: 'Approved synonyms',
    moreSynonyms: '+{{count}} more...',
    categories: 'Categories',
    statusAndProperties: 'Status & properties',
    automaticCapture: 'Automatic capture',
    availableSince: 'Available since {{date}}',
    tabs: {
      availability: 'Availability & data',
      mapping: 'OMOP mapping',
      tags: 'Tags & synonyms',
      documentation: 'Documentation',
      comments: 'Comments',
    },
    documentationTitle: 'Documentation & notes',
    sourceSystem: 'Source system',
    system: 'System',
    updateFrequency: 'Update frequency',
    lastSync: 'Last sync',
    reliability: 'Reliability',
    engineerNotes: 'Data engineer notes',
    noNotes: 'No additional notes available.',
    commentsTitle: 'Comments & discussion',
    addCommentPlaceholder: 'Add a comment...',
    sendComment: 'Send comment',
    noComments: 'No comments available.',
    unknown: 'Unknown',
    usagePatterns: {
      always: 'Always',
      frequent: 'Frequent',
      specific: 'Specific',
      occasional: 'Occasional',
      rare: 'Rare',
      unknown: 'Unknown',
    },
    omopStatus: {
      validated: 'OMOP validated',
      mapped: 'OMOP mapped',
      pending: 'OMOP pending',
      unmapped: 'OMOP not mapped',
    },
  },
  pt: {
    loading: 'Carregando dados do parâmetro...',
    loadError: 'Não foi possível carregar os dados do parâmetro a partir do backend.',
    noDetails: 'Não há dados detalhados disponíveis para este parâmetro.',
    backToCatalog: 'Voltar ao catálogo',
    approvedSynonyms: 'Sinônimos aprovados',
    moreSynonyms: '+{{count}} adicionais...',
    categories: 'Categorias',
    statusAndProperties: 'Status e propriedades',
    automaticCapture: 'Captura automática',
    availableSince: 'Disponível desde {{date}}',
    tabs: {
      availability: 'Disponibilidade e dados',
      mapping: 'Mapeamento OMOP',
      tags: 'Tags e sinônimos',
      documentation: 'Documentação',
      comments: 'Comentários',
    },
    documentationTitle: 'Documentação e observações',
    sourceSystem: 'Sistema de origem',
    system: 'Sistema',
    updateFrequency: 'Frequência de atualização',
    lastSync: 'Última sincronização',
    reliability: 'Confiabilidade',
    engineerNotes: 'Notas do engenheiro de dados',
    noNotes: 'Nenhuma nota adicional disponível.',
    commentsTitle: 'Comentários e discussão',
    addCommentPlaceholder: 'Adicionar comentário...',
    sendComment: 'Enviar comentário',
    noComments: 'Nenhum comentário disponível.',
    unknown: 'Desconhecido',
    usagePatterns: {
      always: 'Sempre',
      frequent: 'Frequente',
      specific: 'Específico',
      occasional: 'Ocasional',
      rare: 'Raro',
      unknown: 'Desconhecido',
    },
    omopStatus: {
      validated: 'OMOP validado',
      mapped: 'OMOP mapeado',
      pending: 'OMOP pendente',
      unmapped: 'OMOP não mapeado',
    },
  },
};

const translateUsagePattern = (value, copy) => {
  const usagePatterns = {
    Immer: copy.usagePatterns.always,
    Häufig: copy.usagePatterns.frequent,
    Spezifisch: copy.usagePatterns.specific,
    Gelegentlich: copy.usagePatterns.occasional,
    Selten: copy.usagePatterns.rare,
    Unbekannt: copy.usagePatterns.unknown,
  };

  return usagePatterns[value] || value;
};

const ParameterDetail = () => {
  const { i18n } = useTranslation();
  const { formatDate } = useAppearance();
  const { parameterId } = useParams();
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [parameterLoading, setParameterLoading] = useState(true);
  const [parameterError, setParameterError] = useState(null);

  const olympAccess = getResolvedOlympAccess();
  const userRole = olympAccess.role || 'user';
  const currentUserName =
    olympAccess.user?.name
    || olympAccess.claims?.name
    || olympAccess.user?.email
    || olympAccess.claims?.email
    || 'Current User';

  const [parameter, setParameter] = useState(null);
  const [tags, setTags] = useState([]);
  const [pendingCategories, setPendingCategories] = useState([]);
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = PARAMETER_DETAIL_COPY[language];
  const locale = language === 'pt' ? 'pt-PT' : language === 'en' ? 'en-US' : 'de-DE';

  const syncTagState = (nextParameter) => {
    const nextTags = buildTagsFromParameter(nextParameter);
    setTags(nextTags.filter(tag => tag.validated || tag.isPrivate));
    setPendingCategories(nextTags.filter(tag => !tag.validated && !tag.isPrivate));
  };

  const applyParameterUpdate = (nextParameter) => {
    setParameter(nextParameter);
    syncTagState(nextParameter);
  };

  useEffect(() => {
    let cancelled = false;

    async function loadParameter() {
      setParameterLoading(true);
      setParameterError(null);

      const result = await getParameter(parameterId);
      if (cancelled) {
        return;
      }

      if (result.source === 'api' && result.parameter) {
        setParameter(result.parameter);
        const nextTags = buildTagsFromParameter(result.parameter);
        setTags(nextTags.filter(tag => tag.validated || tag.isPrivate));
        setPendingCategories(nextTags.filter(tag => !tag.validated && !tag.isPrivate));
        setIsFavorite(Boolean(result.parameter.isFavorite));
        setParameterLoading(false);
        return;
      }

      setParameter(null);
      setTags([]);
      setPendingCategories([]);
      setParameterError(copy.loadError);
      setParameterLoading(false);
    }

    loadParameter();

    return () => {
      cancelled = true;
    };
  }, [parameterId, copy.loadError]);

  // Handler for OMOP mapping updates
  const handleMappingUpdate = (updatedParameter) => {
    applyParameterUpdate(updatedParameter);
  };

  const parameterLookupId = getParameterLookupId(parameter);

  const handleAddToCart = () => {
    console.log('Add to cart:', parameter.id);
    // Show success message
  };

  const handleToggleFavorite = () => {
    setIsFavorite(!isFavorite);
    setParameter(prev => prev ? { ...prev, isFavorite: !isFavorite } : prev);
  };

  const handleCommentSubmit = () => {
    if (commentText.trim()) {
      setParameter(prev => (
        prev
          ? {
              ...prev,
              comments: [
                ...(prev.comments || []),
                {
                  id: `local-${Date.now()}`,
                  author: currentUserName,
                  role: userRole,
                  date: new Date().toISOString().split('T')[0],
                  text: commentText.trim(),
                  replies: [],
                },
              ],
            }
          : prev
      ));
      setCommentText('');
    }
  };

  const handleAddTag = async (tagName, isPrivate, icon) => {
    if (!parameter) {
      return;
    }

    try {
      const result = await createCatalogTag(parameterLookupId, {
        name: tagName,
        icon,
        isPrivate,
      });

      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to persist tag:', error);
    }
  };

  const handleApproveTag = async (tagName, icon) => {
    if (!parameter) {
      return;
    }

    const existingTag = parameter.tagDetails?.find(tag => tag.name === tagName);
    if (!existingTag?.id) {
      const approvedTag = {
        id: null,
        name: tagName,
        icon: icon,
        validated: true,
        isPrivate: false
      };
      setTags([...tags, approvedTag]);
      setPendingCategories(pendingCategories.filter(t => t.name !== tagName));
      return;
    }

    try {
      const result = await approveCatalogTag(parameterLookupId, existingTag.id);
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to approve tag:', error);
    }
  };

  const handleRemoveTag = async (tagName) => {
    if (!parameter) {
      return;
    }

    const existingTag = parameter.tagDetails?.find(tag => tag.name === tagName);
    if (!existingTag?.id) {
      setTags(tags.filter(t => t.name !== tagName));
      setPendingCategories(pendingCategories.filter(t => t.name !== tagName));
      return;
    }

    try {
      const result = await deleteCatalogTag(parameterLookupId, existingTag.id);
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to remove tag:', error);
    }
  };

  const handleAddSynonym = async (synonym) => {
    if (!parameter) {
      return;
    }

    try {
      const result = await createCatalogSynonym(parameterLookupId, synonym);
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to persist synonym:', error);
    }
  };

  const handleRemoveSynonym = async (synonymId) => {
    if (!parameter || !synonymId) {
      return;
    }

    try {
      const result = await deleteCatalogSynonym(parameterLookupId, synonymId);
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to remove synonym:', error);
    }
  };

  const handleApproveSynonym = async (synonymId) => {
    if (!parameter || !synonymId) {
      return;
    }

    try {
      const result = await approveCatalogSynonym(parameterLookupId, synonymId);
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to approve synonym:', error);
    }
  };

  const handleRejectSynonym = async (synonymId) => {
    if (!parameter || !synonymId) {
      return;
    }

    try {
      const result = await rejectCatalogSynonym(parameterLookupId, synonymId);
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to reject synonym:', error);
    }
  };

  const handleVoteSynonym = async (synonymId, vote) => {
    if (!parameter || !synonymId) {
      return;
    }

    try {
      const result = await voteCatalogSynonym(parameterLookupId, synonymId, vote);
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to vote on synonym:', error);
    }
  };

  const handleCommentSynonym = async (synonymId, text) => {
    if (!parameter || !synonymId || !text?.trim()) {
      return;
    }

    try {
      const result = await commentCatalogSynonym(parameterLookupId, synonymId, text.trim());
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to comment on synonym:', error);
    }
  };

  const handleRejectTag = async (tagId) => {
    if (!parameter || !tagId) {
      return;
    }

    try {
      const result = await rejectCatalogTag(parameterLookupId, tagId);
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to reject tag:', error);
    }
  };

  const handleVoteTag = async (tagId, vote) => {
    if (!parameter || !tagId) {
      return;
    }

    try {
      const result = await voteCatalogTag(parameterLookupId, tagId, vote);
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to vote on tag:', error);
    }
  };

  const handleCommentTag = async (tagId, text) => {
    if (!parameter || !tagId || !text?.trim()) {
      return;
    }

    try {
      const result = await commentCatalogTag(parameterLookupId, tagId, text.trim());
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to comment on tag:', error);
    }
  };

  const handleCreateMappingProposal = async (concept) => {
    if (!parameter) {
      return null;
    }

    try {
      const result = await createMappingProposal(parameterLookupId, concept);
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
        return result.parameter;
      }
    } catch (error) {
      console.warn('Failed to create mapping proposal:', error);
    }
    return null;
  };

  const handleApproveMappingProposal = async (proposalId) => {
    if (!parameter || !proposalId) {
      return;
    }

    try {
      const result = await approveMappingProposal(parameterLookupId, proposalId);
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to approve mapping proposal:', error);
    }
  };

  const handleRejectMappingProposal = async (proposalId) => {
    if (!parameter || !proposalId) {
      return;
    }

    try {
      const result = await rejectMappingProposal(parameterLookupId, proposalId);
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to reject mapping proposal:', error);
    }
  };

  const handleVoteMappingProposal = async (proposalId, vote) => {
    if (!parameter || !proposalId) {
      return;
    }

    try {
      const result = await voteMappingProposal(parameterLookupId, proposalId, vote);
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to vote on mapping proposal:', error);
    }
  };

  const handleCommentMappingProposal = async (proposalId, text) => {
    if (!parameter || !proposalId || !text?.trim()) {
      return;
    }

    try {
      const result = await commentMappingProposal(parameterLookupId, proposalId, text.trim());
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to comment on mapping proposal:', error);
    }
  };

  const handleVoteCurrentMapping = async (vote) => {
    if (!parameter) {
      return;
    }

    try {
      const result = await voteCurrentMapping(parameterLookupId, vote);
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to vote on current mapping:', error);
    }
  };

  const handleCommentCurrentMapping = async (text) => {
    if (!parameter || !text?.trim()) {
      return;
    }

    try {
      const result = await commentCurrentMapping(parameterLookupId, text.trim());
      if (result.source === 'api' && result.parameter) {
        applyParameterUpdate(result.parameter);
      }
    } catch (error) {
      console.warn('Failed to comment on current mapping:', error);
    }
  };

  // Icon mapping function
  const getIconComponent = (iconName) => {
    const iconMap = {
      'monitor_heart': MonitorHeartIcon,
      'local_hospital': LocalHospitalIcon,
      'vaccines': VaccinesIcon,
      'science': ScienceIcon
    };
    return iconMap[iconName] || null;
  };

  // Pastel colors for tags
  const getChipColor = (isValidated, isPrivate) => {
    if (isPrivate) return { bg: '#e3f2fd', border: '#42a5f5', color: '#1565c0' };
    if (isValidated) return { bg: '#e8f5e9', border: '#66bb6a', color: '#2e7d32' };
    return { bg: '#fff3e0', border: '#ffb74d', color: '#f57c00' };
  };

  // OMOP status styling
  const getOmopStatusConfig = (status) => {
    switch (status) {
      case 'validated':
        return {
          icon: CheckCircleIcon,
          label: copy.omopStatus.validated,
          bg: '#e8f5e9',
          border: '#66bb6a',
          color: '#2e7d32'
        };
      case 'mapped':
        return {
          icon: CheckCircleIcon,
          label: copy.omopStatus.mapped,
          bg: '#e3f2fd',
          border: '#42a5f5',
          color: '#1565c0'
        };
      case 'pending':
        return {
          icon: WarningIcon,
          label: copy.omopStatus.pending,
          bg: '#fff3e0',
          border: '#ffb74d',
          color: '#f57c00'
        };
      default:
        return {
          icon: HelpOutlineIcon,
          label: copy.omopStatus.unmapped,
          bg: '#ffebee',
          border: '#ef5350',
          color: '#c62828'
        };
    }
  };

  if (parameterLoading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Typography variant="h6" color="textSecondary">
            {copy.loading}
          </Typography>
        </Box>
      </MainLayout>
    );
  }

  if (parameterError) {
    return (
      <MainLayout>
        <Box sx={{ flexGrow: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/catalog')}
            sx={{ mb: 2 }}
          >
            {copy.backToCatalog}
          </Button>
          <Alert severity="error">{parameterError}</Alert>
        </Box>
      </MainLayout>
    );
  }

  if (!parameter) {
    return (
      <MainLayout>
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="warning">{copy.noDetails}</Alert>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1 }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/catalog')}
          sx={{ mb: 2 }}
        >
          {copy.backToCatalog}
        </Button>

        {/* Header Section */}
        <HeaderSection elevation={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h4" fontWeight="bold">
                  {parameter.name}
                </Typography>

                {/* Synonyms Indicator */}
                {parameter.approvedSynonyms && parameter.approvedSynonyms.length > 0 && (
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          {copy.approvedSynonyms}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {parameter.approvedSynonyms.slice(0, 5).map((syn, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={syn.language.toUpperCase()}
                                size="small"
                                sx={{
                                  height: 16,
                                  fontSize: '0.6rem',
                                  backgroundColor: 'rgba(255,255,255,0.2)'
                                }}
                              />
                              <Typography variant="body2">{syn.term}</Typography>
                            </Box>
                          ))}
                          {parameter.approvedSynonyms.length > 5 && (
                            <Typography variant="caption" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                              {copy.moreSynonyms.replace('{{count}}', parameter.approvedSynonyms.length - 5)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    }
                    arrow
                    placement="bottom-start"
                  >
                    <Badge
                      badgeContent={parameter.approvedSynonyms.length}
                      color="primary"
                      sx={{
                        cursor: 'help',
                        '& .MuiBadge-badge': {
                          fontSize: '0.65rem',
                          height: 18,
                          minWidth: 18
                        }
                      }}
                    >
                      <TranslateIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
                    </Badge>
                  </Tooltip>
                )}

                <FavoriteButton
                  isFavorite={isFavorite}
                  onClick={handleToggleFavorite}
                  size="medium"
                />
              </Box>
              <Typography variant="body1" color="textSecondary" paragraph>
                {parameter.description}
              </Typography>

              {/* Kategorien Section */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary" fontWeight="bold" display="block" sx={{ mb: 1 }}>
                  {copy.categories}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {/* Validated/Private Categories (green/blue) */}
                  {tags.map((tag, index) => {
                    const CustomIconComponent = tag.icon ? getIconComponent(tag.icon) : null;
                    const chipColors = getChipColor(tag.validated, tag.isPrivate);

                    return (
                      <Chip
                        key={`validated-${index}`}
                        icon={
                          tag.isPrivate ? (
                            <LockIcon fontSize="small" sx={{ color: chipColors.color }} />
                          ) : CustomIconComponent ? (
                            <CustomIconComponent fontSize="small" sx={{ color: chipColors.color }} />
                          ) : (
                            <PublicIcon fontSize="small" sx={{ color: chipColors.color }} />
                          )
                        }
                        label={tag.name}
                        size="medium"
                        sx={{
                          backgroundColor: chipColors.bg,
                          borderStyle: 'solid',
                          borderWidth: 1,
                          borderColor: chipColors.border,
                          color: chipColors.color,
                          '& .MuiChip-label': {
                            color: chipColors.color
                          }
                        }}
                      />
                    );
                  })}

                  {/* Pending Categories (yellow/orange) */}
                  {pendingCategories.map((tag, index) => {
                    const CustomIconComponent = tag.icon ? getIconComponent(tag.icon) : null;
                    const chipColors = getChipColor(tag.validated, tag.isPrivate);

                    return (
                      <Chip
                        key={`pending-${index}`}
                        icon={
                          CustomIconComponent ? (
                            <CustomIconComponent fontSize="small" sx={{ color: chipColors.color }} />
                          ) : (
                            <PublicIcon fontSize="small" sx={{ color: chipColors.color }} />
                          )
                        }
                        label={tag.name}
                        size="medium"
                        sx={{
                          backgroundColor: chipColors.bg,
                          borderStyle: 'solid',
                          borderWidth: 1,
                          borderColor: chipColors.border,
                          color: chipColors.color,
                          '& .MuiChip-label': {
                            color: chipColors.color
                          }
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>

              {/* Status Section */}
              <Box>
                <Typography variant="caption" color="textSecondary" fontWeight="bold" display="block" sx={{ mb: 1 }}>
                  {copy.statusAndProperties}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  {(() => {
                    const omopStatus = parameter.omopMapping?.status || parameter.omopStatus || 'unmapped';
                    const omopConfig = getOmopStatusConfig(omopStatus);
                    const OmopIconComponent = omopConfig.icon;
                    return (
                      <Chip
                        icon={<OmopIconComponent fontSize="small" sx={{ color: omopConfig.color }} />}
                        label={omopConfig.label}
                        size="small"
                        sx={{
                          backgroundColor: omopConfig.bg,
                          borderStyle: 'solid',
                          borderWidth: 1,
                          borderColor: omopConfig.border,
                          color: omopConfig.color,
                          '& .MuiChip-label': {
                            color: omopConfig.color,
                            fontWeight: 500
                          }
                        }}
                      />
                    );
                  })()}
                  <SensitivityBadge level={parameter.sensitivity} />
                  {parameter.usagePattern && (
                    <Chip
                      label={translateUsagePattern(parameter.usagePattern, copy)}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: 'rgba(0,0,0,0.23)',
                        color: 'text.primary'
                      }}
                    />
                  )}
                  <Chip
                    label={copy.automaticCapture}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: 'rgba(0,0,0,0.23)',
                      color: 'text.primary'
                    }}
                  />
                  <Typography variant="caption" color="textSecondary">
                    {copy.availableSince.replace('{{date}}', parameter.availableSince || '–')}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </HeaderSection>

        {/* Tabs Navigation */}
        <Paper elevation={1} sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={copy.tabs.availability} />
            <Tab label={copy.tabs.mapping} />
            <Tab label={copy.tabs.tags} />
            <Tab label={copy.tabs.documentation} />
            <Tab label={copy.tabs.comments} />
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        <Box sx={{ mb: 3 }}>
          {/* Tab 0: Verfügbarkeit & Daten */}
          {activeTab === 0 && (
            <Paper elevation={2} sx={{ p: 3 }}>
              {parameter.frequencyMetrics && (
                <DataExplorationDetail
                  data={{
                    ...parameter.frequencyMetrics,
                    similarParameters: parameter.similarParameters || parameter.displayData?.similarParameters || [],
                    units: parameter.units || [],
                  }}
                  sensitivity={parameter.sensitivity}
                  usagePattern={parameter.usagePattern}
                  parameterName={parameter.name}
                />
              )}

              {/* **Die vollstaendige Werteverteilung gehoert hierher (#244).**
                  Der aufgeklappte Bereich in der Liste ist eine Triage-Flaeche
                  und zeigt seit #244 nur noch die drei haeufigsten Werte; die
                  ganze Liste (bis 25 Eintraege) steht hier, neben den anderen
                  Verteilungen dieser Ansicht.

                  Bis heute wurde sie hier **gar nicht** gezeigt: Tab 0 reichte
                  an `DataExplorationDetail` nur `frequencyMetrics` und
                  `similarParameters` durch. Das war kein Verschieben eines
                  vorhandenen Bausteins, sondern neue Verkabelung. */}
              {(parameter.valueDistribution?.length > 0 || parameter.valueDistributionReason) && (
                <Box sx={{ mt: 3 }}>
                  <WerteverteilungCard
                    verteilung={parameter.valueDistribution}
                    grund={parameter.valueDistributionReason}
                  />
                </Box>
              )}
            </Paper>
          )}

          {/* Tab 1: OMOP Mapping */}
          {activeTab === 1 && parameter && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <OmopMappingDetail
                parameter={parameter}
                onMappingUpdate={handleMappingUpdate}
                userRole={userRole}
                currentUserName={currentUserName}
                onCreateProposal={handleCreateMappingProposal}
                onApproveProposal={handleApproveMappingProposal}
                onRejectProposal={handleRejectMappingProposal}
                onVoteProposal={handleVoteMappingProposal}
                onCommentProposal={handleCommentMappingProposal}
                onVoteCurrentMapping={handleVoteCurrentMapping}
                onCommentCurrentMapping={handleCommentCurrentMapping}
              />
            </Paper>
          )}

          {/* Tab 2: Tags & Synonyme */}
          {activeTab === 2 && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <TagsSynonymsDetail
                tags={tags}
                tagDetails={parameter.tagDetails || []}
                synonyms={parameter.synonyms || []}
                availableTags={[...new Set([...(parameter.tags || []), ...tags.map(t => t.name)])]}
                currentUser={currentUserName}
                userRole={userRole}
                onAddTag={handleAddTag}
                onApproveTag={handleApproveTag}
                onRejectTag={handleRejectTag}
                onVoteTag={handleVoteTag}
                onCommentTag={handleCommentTag}
                onRemoveTag={handleRemoveTag}
                onAddSynonym={handleAddSynonym}
                onApproveSynonym={handleApproveSynonym}
                onRejectSynonym={handleRejectSynonym}
                onVoteSynonym={handleVoteSynonym}
                onCommentSynonym={handleCommentSynonym}
                onRemoveSynonym={handleRemoveSynonym}
              />
            </Paper>
          )}

          {/* Tab 3: Dokumentation */}
          {activeTab === 3 && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                <InfoIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                {copy.documentationTitle}
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                {copy.sourceSystem}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="body2"><strong>{copy.system}:</strong> {parameter.sourceSystemInfo?.name || parameter.dataMapping?.system || parameter.sourceSystem || '–'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2"><strong>{copy.updateFrequency}:</strong> {parameter.sourceSystemInfo?.updateFrequency || '–'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2"><strong>{copy.lastSync}:</strong> {parameter.sourceSystemInfo?.lastSync || '–'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2"><strong>{copy.reliability}:</strong> {parameter.sourceSystemInfo?.reliability || '–'}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                {copy.engineerNotes}
              </Typography>
              {parameter.engineerNotes && parameter.engineerNotes.length > 0 ? (
                <List>
                  {parameter.engineerNotes.map((note, index) => (
                    <ListItem key={index} alignItems="flex-start" sx={{ backgroundColor: '#fff3e0', mb: 1, borderRadius: 1 }}>
                      <ListItemText
                        primary={note.note}
                        secondary={`${note.author || copy.unknown} - ${note.date || '–'}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info">{copy.noNotes}</Alert>
              )}
            </Paper>
          )}

          {/* Tab 4: Kommentare */}
          {activeTab === 4 && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                <CommentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                {copy.commentsTitle}
              </Typography>
              <Divider sx={{ mb: 3 }} />

              {/* Comment Input */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder={copy.addCommentPlaceholder}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  sx={{ mb: 1 }}
                />
                <Button
                  variant="contained"
                  endIcon={<SendIcon />}
                  onClick={handleCommentSubmit}
                  disabled={!commentText.trim()}
                >
                  {copy.sendComment}
                </Button>
              </Box>

              {/* Comments List */}
              {parameter.comments && parameter.comments.length > 0 ? (
                <List>
                  {parameter.comments.map(comment => (
                    <Box key={comment.id} sx={{ mb: 2 }}>
                      <ListItem alignItems="flex-start" sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                        <Avatar sx={{ mr: 2 }}>{comment.author?.[0] || '?'}</Avatar>
                        <ListItemText
                          primary={
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold" component="span">
                                {comment.author}
                              </Typography>
                              {comment.role && <Chip label={comment.role} size="small" sx={{ ml: 1, height: 20 }} />}
                              <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                {comment.date ? formatDate(comment.date) : '–'}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {comment.text}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {comment.replies && comment.replies.map((reply, idx) => (
                        <Box key={idx} sx={{ ml: 8, mt: 1 }}>
                          <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: 'white' }}>
                            <Typography variant="caption" fontWeight="bold">
                              {reply.author}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                              {reply.date ? formatDate(reply.date) : '–'}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {reply.text}
                            </Typography>
                          </Paper>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </List>
              ) : (
                <Alert severity="info">{copy.noComments}</Alert>
              )}
            </Paper>
          )}
        </Box>
      </Box>
    </MainLayout>
  );
};

export default ParameterDetail;
